(() => {
  class CollectionProducts extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.loadRemaining();
      });
      this.observer.observe(this);
    }
    async loadRemaining() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.abortController = new AbortController();
      try {
        while (this.dataset.nextUrl && this.isConnected) {
          const response = await fetch(this.dataset.nextUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error('Collection request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = html.querySelector('navigation-collection-products');
          if (!next) throw new Error('Collection response missing');
          const grid = this.querySelector('.navigation-collection-grid');
          if (this.hasAttribute('data-reset-products')) {
            grid.replaceChildren();
            this.removeAttribute('data-reset-products');
          }
          grid.append(...next.querySelector('.navigation-collection-grid').children);
          this.dataset.nextUrl = next.dataset.nextUrl || '';
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.addEventListener('pointerenter', () => this.loadRemaining(), { once: true });
          this.addEventListener('focusin', () => this.loadRemaining(), { once: true });
        }
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.abortController?.abort();
    }
  }
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = [...this.querySelectorAll('[data-collection-tab]')];
      this.panels = [...this.querySelectorAll('[data-collection-panel]')];
      this.querySelector('[data-tab-list]').setAttribute('role', 'tablist');
      this.querySelector('[data-tab-list]').setAttribute('aria-orientation', 'vertical');
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.panel);
        tab.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'touch') this.activate(tab);
        }, options);
        tab.addEventListener('focus', () => this.activate(tab), options);
        tab.addEventListener('click', event => { event.preventDefault(); this.activate(tab); }, options);
        tab.addEventListener('keydown', event => {
          const index = this.tabs.indexOf(tab);
          let next;
          if (event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); this.tabs[next].focus(); }
          if (event.key === ' ') { event.preventDefault(); this.activate(tab); }
        }, options);
      });
      this.panels.forEach(panel => { panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0; });
      this.activate(this.tabs[0]);
      this.details = this.closest('details');
      this.details?.addEventListener('toggle', () => {
        if (!this.details.open) return;
        this.activate(this.tabs[0]);
        this.closest('.header-menu-nav')?.querySelectorAll('.first-level-drop > details[open]').forEach(other => {
          if (other !== this.details) other.removeAttribute('open');
        });
      }, options);
      this.details?.querySelector('summary')?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        this.details.setAttribute('open', '');
        this.tabs[0]?.focus();
      }, options);
      this.details?.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        const summary = this.details.querySelector('summary');
        summary.focus();
        this.details.removeAttribute('open');
      }, options);
    }
    activate(tab) {
      if (!tab) return;
      this.tabs.forEach(item => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.id !== tab.dataset.panel; });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }
  class CollectionAccordion extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-category-toggle]')];
      this.buttons.forEach(button => {
        this.setOpen(button, false);
        button.addEventListener('click', () => {
          const opening = button.getAttribute('aria-expanded') !== 'true';
          this.buttons.forEach(other => this.setOpen(other, other === button && opening));
        }, { signal: this.controller.signal });
      });
    }
    setOpen(button, open) {
      button.setAttribute('aria-expanded', String(open));
      button.nextElementSibling.hidden = !open;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }
  if (!customElements.get('navigation-collection-tabs')) customElements.define('navigation-collection-tabs', CollectionTabs);
  if (!customElements.get('navigation-collection-accordion')) customElements.define('navigation-collection-accordion', CollectionAccordion);
  if (!customElements.get('navigation-collection-products')) customElements.define('navigation-collection-products', CollectionProducts);
})();
